import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { PaymentResult, type PaymentParams, type PaymentProps } from "@/modules/billing/payment/PaymentParams.ts";
import {
    checkForPaymentTargetSuccess,
    linkSendData,
    pollForSuccess,
    redirectToGatewayAndWaitForResponse,
} from "@/modules/billing/payment/paymentUtils.ts";
import { t_StartingPayment } from "@/modules/billing/progressLabels.tsx";
import { QrCodeDisplay, type QrCode } from "@/modules/billing/QrCodeDisplay.tsx";
import { showWarn } from "@/modules/error/errorStream.ts";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import type { PaymentIntentResult, Stripe } from "@stripe/stripe-js";
import type {
    MethodConfig,
    StripeWalletConfig,
    StripeWalletRedirectSendDataResponse,
} from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { unreachableSwitchCase } from "@warrenio/utils/unreachable";
import { useImperativeHandle, useState } from "react";

interface AlipayHandleRedirect {
    type: "alipay_handle_redirect";
    alipay_handle_redirect: {
        url: string;
    };
}

export default function StripeLinkPaymentForm({
    actionsRef,
    method: { key },
    stripe,
}: { config: StripeWalletConfig; method: MethodConfig } & PaymentProps & { stripe: Promise<Stripe> }) {
    //#region Hooks
    const [qrCode, setQrCode] = useState<QrCode | null>(null);

    const apiClient = useApiClient();
    const signal = useUnmountSignal();

    useImperativeHandle(actionsRef, () => ({
        needsPopUp(_params) {
            return key !== "wechat_pay";
        },

        async makePayment(params: PaymentParams) {
            const { progress, popup } = params;

            progress(t_StartingPayment);
            const { data } = await linkSendData<StripeWalletRedirectSendDataResponse>(
                apiClient,
                params,
                "stripe_wallet",
                { paymentMethod: key },
            );

            const { client_secret, return_url, pay_intent_id } = data;

            const stripeApi = await stripe;
            let intentConfirmResult: PaymentIntentResult;

            switch (key) {
                case "grabpay":
                    intentConfirmResult = await stripeApi.confirmGrabPayPayment(
                        client_secret,
                        {
                            return_url,
                        },
                        { handleActions: false },
                    );
                    break;
                case "alipay":
                    intentConfirmResult = await stripeApi.confirmAlipayPayment(
                        client_secret,
                        { return_url },
                        { handleActions: false },
                    );
                    break;
                case "wechat_pay":
                    intentConfirmResult = await stripeApi.confirmWechatPayPayment(
                        client_secret,
                        {
                            payment_method_options: {
                                wechat_pay: {
                                    client: "web",
                                },
                            },
                        },
                        {
                            handleActions: false,
                        },
                    );
                    break;
                case "pay_by_bank":
                    intentConfirmResult = await stripeApi.confirmPayByBankPayment(
                        client_secret,
                        {
                            return_url,
                            payment_method: {
                                billing_details: {},
                                pay_by_bank: {},
                            },
                        },
                        { handleActions: false },
                    );
                    break;
                default:
                    unreachableSwitchCase(key);
            }

            const paymentIntent = notNull(intentConfirmResult.paymentIntent, "paymentIntent");
            const nextAction = notNull(paymentIntent.next_action, "next_action");

            const redirectUrl =
                nextAction.type === "alipay_handle_redirect"
                    ? (nextAction as AlipayHandleRedirect).alipay_handle_redirect.url
                    : nextAction.redirect_to_url?.url;

            const reference = `stripe_wallet::${pay_intent_id}`;
            const check = async () =>
                await checkForPaymentTargetSuccess({
                    apiClient,
                    reference,
                    params,
                    signal,
                });

            if (redirectUrl) {
                return await redirectToGatewayAndWaitForResponse({ popup, progress, redirectUrl, signal, check });
            } else if (nextAction.type === "wechat_pay_display_qr_code") {
                progress(null);

                const qrCode = notNull(nextAction.wechat_pay_display_qr_code, "wechat_pay_display_qr_code");
                setQrCode({ qrcode_url: notNull(qrCode.image_data_url, "image_data_url") });

                if (qrCode.data) {
                    console.log("Confirm payment here for testing: %c%s", "text-decoration: underline;", qrCode.data);
                }

                return PaymentResult.CLOSE_MANUALLY; // don't poll result when QR code is displayed
            } else {
                showWarn("Unhandled nextAction type: %s", nextAction.type);
            }

            return await pollForSuccess({ signal, popup, check });
            // cSpell:disable-next-line
            // /app/payment_return/?payment_intent=pi_3PzYfxI7ApJ8GXEs0BsnRuBQ&payment_intent_client_secret=pi_3PzYfxI7ApJ8GXEs0BsnRuBQ_secret_hyUFUpJtP7JvAMWL5AvGcfb8H&redirect_status=succeeded&stripe_wallet_return=1
        },
    }));
    //#endregion

    if (qrCode) {
        return <QrCodeDisplay qrCode={qrCode} />;
    }

    return null;
}
